import React from 'react';

import styled from '@emotion/styled';

import { Box, COLORS, Modal } from '@clutter/clean';

import {
  CheckoutModal,
  ModalCloseButton,
  ModalContainer,
  ModalScrollableBody,
  ModalTitle,
  SwipeableBottomSheet,
} from '@root/components/checkout/steps/subcomponents/modal_shared';

import { useIsomorphicBreakpoints } from '@utils/hooks/use_breakpoints';

import { PricingContent } from './pricing_modal/pricing_content';
import { ProhibitedContent } from './pricing_modal/prohibited_content';

const Container = styled.div`
  box-sizing: border-box;
  position: relative;
  /* Explicit width so react-modal content isn’t shrink-wrapped; align with funnel 24px gutters */
  width: min(720px, calc(100vw - 48px));
  max-width: min(720px, calc(100vw - 48px));
  margin: 0 auto;
  border-radius: 16px;
  padding: 0 24px;
  color: ${COLORS.tealDark};
  background: ${COLORS.cloud};
  /* Stay inside the viewport when the shell is vertically centered (checkout footer/header). */
  max-height: min(85vh, 85dvh, calc(100vh - 120px), calc(100dvh - 120px));
  overflow-x: hidden;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
`;

export const PricingModal: React.FC<{
  isOpen: boolean;
  onClose(): void;
  /** Align shell with funnel `CheckoutModal` / `ModalContainer` (payment sidebar). */
  visualVariant?: 'default' | 'funnelCheckout';
}> = ({ isOpen, onClose, visualVariant = 'default' }) => {
  const { isMobile } = useIsomorphicBreakpoints();

  if (visualVariant === 'funnelCheckout') {
    const body = (
      <ModalScrollableBody>
        <Box.Flex flexDirection="column" gap="18px">
          <ModalTitle>Disposal Pricing</ModalTitle>
          <PricingContent showEmbeddedTitle={false} />
          <ProhibitedContent visualVariant="funnelCheckout" />
        </Box.Flex>
      </ModalScrollableBody>
    );

    if (!isOpen) {
      return null;
    }

    if (isMobile) {
      return (
        <>
          <div
            role="presentation"
            aria-hidden="true"
            tabIndex={-1}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.5)',
              /* Below SwipeableBottomSheet (z-index 1000); matches funnel AppointmentEditModal. */
              zIndex: 999,
            }}
            onClick={onClose}
          />
          <SwipeableBottomSheet onClose={onClose}>{body}</SwipeableBottomSheet>
        </>
      );
    }

    return (
      <CheckoutModal isOpen={isOpen} onClose={onClose}>
        <ModalContainer>
          <ModalCloseButton onClose={onClose} />
          {body}
        </ModalContainer>
      </CheckoutModal>
    );
  }

  return (
    <Modal
      isOpen={isOpen}
      handleModalClose={onClose}
      includeCloseButton
      overflowY="auto"
    >
      <Container>
        <Box padding="36px 0 24px">
          <Box margin="0 0 24px">
            <PricingContent />
          </Box>
          <ProhibitedContent />
        </Box>
      </Container>
    </Modal>
  );
};
